import { useCallback } from 'react';
import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import { useNavigateToMainTabs } from '../../../../navigation';

export const useHomeNavigation = (orderId: string | null = null) => {
  const { track } = useTelemetry();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();

  // ─── Navigation ──────────────────────────────────────────────────────

  const navigateToMainTabs = useNavigateToMainTabs(navigation);

  const navigateToAccount = useCallback(() => {
    track('home.account.cta_tapped');

    if (isExpoRouterEnabled) {
      router?.navigate('/account');

      if (router?.canDismiss()) {
        router?.dismissAll(); // reset account stack to start from the initial route
      }

      return;
    }

    void navigateToMainTabs('AccountTab', { screen: 'AccountMenu' });
  }, [isExpoRouterEnabled, router, navigateToMainTabs, track]);

  const navigateToLoyalty = useCallback(() => {
    track('home.loyalty.cta_tapped');

    if (isExpoRouterEnabled) {
      router?.navigate('/sg-rewards');
      return;
    }

    void navigateToMainTabs('LoyaltyTab', { screen: 'LoyaltyHome' });
  }, [isExpoRouterEnabled, router, navigateToMainTabs, track]);

  const navigateToSignIn = useCallback(() => {
    track('home.banner.cta_tapped', { destination: 'SignIn' });

    if (isExpoRouterEnabled) {
      router?.navigate({
        pathname: '/(auth)/join-or-sign-in',
        params: { redirect: 'home' },
      });
      return;
    }

    navigation?.navigate('Auth', {
      screen: 'JoinOrSignIn',
      params: { redirect: 'home' },
    });
  }, [isExpoRouterEnabled, navigation, router, track]);

  const navigateToOrderStatus = useCallback(() => {
    if (!orderId) return;

    track('home.banner.cta_tapped', { destination: 'OrderStatus' });

    if (isExpoRouterEnabled) {
      router?.navigate({
        pathname: '/order-status/[orderId]',
        params: { orderId },
      });
      return;
    }

    navigation?.navigate('Modal', {
      screen: 'OrderStatus',
      params: { orderId },
    });
  }, [isExpoRouterEnabled, navigation, orderId, router, track]);

  const navigateToRateOrder = useCallback(() => {
    if (!orderId) return;

    track('home.banner.cta_tapped', { destination: 'RateOrder' });

    if (isExpoRouterEnabled) {
      router?.navigate({
        pathname: '/feedback/[orderId]',
        params: { orderId },
      });
      return;
    }

    navigation?.navigate('Modal', {
      screen: 'RateOrder',
      params: { orderId },
    });
  }, [isExpoRouterEnabled, navigation, orderId, router, track]);

  return {
    navigateToAccount,
    navigateToLoyalty,
    navigateToSignIn,
    navigateToOrderStatus,
    navigateToRateOrder,
  };
};
